import React, { useEffect, useState } from "react";
import { ContentItem } from "@/types/content";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Lock, Globe, Link as LinkIcon, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";

interface ChatShareDialogProps {
  chat: ContentItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVisibilityChange?: (chatId: string, visibility: "private" | "public") => void;
}

export const ChatShareDialog = ({
  chat,
  open,
  onOpenChange,
  onVisibilityChange,
}: ChatShareDialogProps) => {
  const { isRtl } = useTranslation();
  const [localVisibility, setLocalVisibility] = useState<"private" | "public">(
    chat?.visibility ?? "private"
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLocalVisibility(chat?.visibility ?? "private");
  }, [chat?.visibility]);

  const handleSelect = async (visibility: "private" | "public") => {
    if (!chat) return;

    setLocalVisibility(visibility);

    const { error } = await supabase
      .from("content")
      .update({ visibility })
      .eq("id", chat.id);

    if (error) {
      toast.error("Failed to update visibility");
      setLocalVisibility(chat.visibility ?? "private");
      return;
    }

    toast.success(`Chat is now ${visibility}`);
    if (onVisibilityChange) {
      onVisibilityChange(chat.id, visibility);
    }
  };

  const handleCopy = () => {
    if (!chat) return;
    const url = `${window.location.origin}/chat/${chat.id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!chat) return null;

  const shareUrl = `${window.location.origin}/chat/${chat.id}`;
  const isPublic = localVisibility === "public";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-xl p-4 pb-0" dir={isRtl ? "rtl" : "ltr"}>
      
        <DialogHeader>
          <DialogTitle className="text-base text-foreground/70">
            Invite your friends
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-4 pb-0">
          <div className="border border-border rounded-2xl overflow-hidden">
            {/* Private */}
            <button
              className={cn(
                "flex w-full items-center justify-between p-4 hover:bg-muted/50 transition-colors",
                localVisibility === "private" && "bg-muted/40"
              )}
              onClick={() => handleSelect("private")}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-full">
                  <Lock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-left space-y-0.5">
                  <div className="text-sm font-medium text-foreground">Private</div>
                  <div className="text-xs text-muted-foreground">
                    Viewable by yourself only
                  </div>
                </div>
              </div>
              {localVisibility === "private" && (
                <Check className="w-5 h-5 text-primary" />
              )}
            </button>

            {/* Public */}
            <button
              className={cn(
                "flex w-full items-center justify-between p-4 border-t border-border hover:bg-muted/50 transition-colors",
                localVisibility === "public" && "bg-muted/40"
              )}
              onClick={() => handleSelect("public")}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-full">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-left space-y-0.5">
                  <div className="text-sm font-medium text-foreground">Public</div>
                  <div className="text-xs text-muted-foreground">
                    Anyone with link can view
                  </div>
                </div>
              </div>
              {localVisibility === "public" && (
                <Check className="w-5 h-5 text-primary" />
              )}
            </button>
          </div>
        </div>

        <div className="py-4 px-1">
          <button
            onClick={handleCopy}
            disabled={!isPublic}
            className={cn(
              "w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center transition-colors",
              isPublic
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            <LinkIcon className="h-4 w-4 mr-1.5" />
            {copied ? "Copied!" : "Copy Public Link"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
